import type { TUser } from '@roko/ui/types/common'
import type { StateCreator } from 'zustand'
import { create } from 'zustand'
import type { PersistOptions } from 'zustand/middleware'
import { persist } from 'zustand/middleware'

import { getEntraAuthorizeUrl, getEntraProfileFromTokens } from '@/auth/entraConfig'
import { clearTokens, getAccessToken, hasValidEntraAccessToken, setAccessToken } from '@/data/api/token'

type TInitialState = {
  user: TUser | null
  /** App is always usable; guests are public session-only. */
  isAuthenticated: boolean
  /** Entra-registered user — conversation history is available. */
  isRegistered: boolean
}

const GUEST_USER: TUser = { userName: 'Guest' }

const GUEST_STATE: TInitialState = {
  user: GUEST_USER,
  isAuthenticated: true,
  isRegistered: false,
}

interface IAuthStore extends TInitialState {
  completeEntraLogin: (params: { accessToken: string; userName: string; email?: string }) => void
  loginWithEntra: () => Promise<void>
  logout: () => Promise<void>
}

type TAuthPersist = (
  config: StateCreator<IAuthStore>,
  options: PersistOptions<IAuthStore, Pick<IAuthStore, 'user' | 'isAuthenticated' | 'isRegistered'>>
) => StateCreator<IAuthStore>

const userFromAccessToken = (): TUser | null => {
  const accessToken = getAccessToken()
  if (!accessToken || !hasValidEntraAccessToken()) {
    return null
  }

  const profile = getEntraProfileFromTokens({ access_token: accessToken })
  return { userName: profile.userName, email: profile.email }
}

const restoreFromToken = (): TInitialState => {
  const user = userFromAccessToken()
  if (user) {
    return {
      user,
      isAuthenticated: true,
      isRegistered: true,
    }
  }
  return GUEST_STATE
}

export const useAuthStore = create<IAuthStore>()(
  (persist as TAuthPersist)(
    (set) => ({
      ...restoreFromToken(),
      completeEntraLogin: ({ accessToken, userName, email }) => {
        setAccessToken(accessToken)
        set({ user: { userName, email }, isAuthenticated: true, isRegistered: true })
      },
      loginWithEntra: async () => {
        window.location.assign(await getEntraAuthorizeUrl())
      },
      logout: async () => {
        clearTokens()
        set(GUEST_STATE)
      },
    }),
    {
      name: 'auth-storage-app',
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        isRegistered: state.isRegistered,
      }),
      onRehydrateStorage: () => () => {
        useAuthStore.setState(restoreFromToken())
      },
    }
  )
)
